<template>
  <div>
    <div v-if="loading" class="text-center py-5">Cargando...</div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-else class="row g-4">
      <div
        class="col-12 col-sm-6 col-md-4 col-lg-3"
        v-for="card in paged"
        :key="card._id"
      >
        <div class="card evi-card h-100">
          <div class="pswp-gallery" :id="galleryId(card)">
            <a
              v-if="card.organizacion.fotos?.length"
              class="evi-cover-link"
              :href="card.organizacion.fotos[0]"
              @click.prevent="open(galleryId(card), 0)"
            >
              <div class="evi-cover position-relative">
                <img
                  :src="card.organizacion.fotos[0]"
                  class="w-100 h-100"
                  alt="foto principal"
                  loading="lazy"
                  style="object-fit: cover"
                  @error="onImgError"
                />
                <div class="evi-cover-overlay"></div>

                <span
                  class="badge evi-tipo"
                  :class="tipoClass(card.organizacion.tipo)"
                >
                  {{ capitalize(card.organizacion.tipo) }}
                </span>
                <span class="badge evi-count">
                  {{ card.organizacion.fotos.length }} fotos
                </span>

                <div class="evi-cta">Ver galería</div>
              </div>
            </a>

            <div class="card-body">
              <h6 class="card-title evi-title mb-2">
                {{ card.organizacion.nombre }}
              </h6>

              <ul class="list-unstyled small mb-0 evi-meta">
                <li class="mb-1">
                  <strong>Voluntario:</strong>
                  {{ card.usuario.nombres }} {{ card.usuario.apellidos }}
                </li>
                <li class="mb-1">
                  <strong>Rol:</strong> {{ card.usuario.rol }}
                </li>
                <li class="mb-1 text-truncate">
                  <strong>Correo:</strong> {{ card.usuario.email }}
                </li>
              </ul>
            </div>

            <div class="card-footer bg-white">
              <div class="evi-thumbs d-flex gap-2 flex-wrap">
                <a
                  v-for="(img, i) in card.organizacion.fotos"
                  :key="`${card._id}-${i}`"
                  :href="img"
                  class="evi-thumb-link"
                  @click.prevent="open(galleryId(card), i)"
                >
                  <img
                    :src="img"
                    class="evi-thumb-img"
                    alt="miniatura"
                    loading="lazy"
                    @error="onImgError"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="d-flex justify-content-center align-items-center gap-3 mt-4 paginador"
    >
      <button
        class="btn btn-colgate"
        :disabled="currentPage === 1 || loading"
        @click="prevPage"
      >
        Anterior
      </button>
      <div class="fw-semibold">
        Página {{ currentPage }} de {{ totalPages || 1 }}
      </div>
      <button
        class="btn btn-colgate"
        :disabled="currentPage === totalPages || loading || totalPages === 0"
        @click="nextPage"
      >
        Siguiente
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from "vue";
import { obtenerEvidencias } from "../services/api";
import { usePhotoSwipe } from "../composables/usePhotoSwipe";
import { capitalize, tipoClass } from "../utils/helpers";

const evidencias = ref([]);
const loading = ref(true);
const error = ref("");

const pageSize = 12;
const currentPage = ref(1);
const totalPages = computed(() =>
  Math.ceil(evidencias.value.length / pageSize)
);
const paged = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return evidencias.value.slice(start, start + pageSize);
});

const { init: initLightbox, open } = usePhotoSwipe();

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

const onImgError = (e) => {
  e.target.style.display = "none";
};

const galleryId = (card) => `gallery-${card._id}`;

watch(paged, async () => {
  await nextTick();
  initLightbox();
});

onMounted(async () => {
  try {
    const { data } = await obtenerEvidencias();
    evidencias.value = data;
    await nextTick();
    initLightbox();
  } catch (e) {
    error.value = e?.response?.data?.message || "Error al cargar evidencias.";
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.btn-colgate {
  background: #e4002b;
  color: #fff;
  border: none;
  font-weight: 700;
  padding: 0.6rem 1.2rem;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(228, 0, 43, 0.25);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.btn-colgate:hover {
  background: #c70024;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(228, 0, 43, 0.35);
}
.btn-colgate:disabled {
  background: #ef9aa8;
  color: #fff;
  opacity: 1;
}
.paginador {
  user-select: none;
}

.evi-card {
  border: 0;
  border-radius: 22px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.08);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}
.evi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.12);
}

.evi-cover {
  width: 100%;
  aspect-ratio: 4 / 3;
  position: relative;
}
.evi-cover-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0.35) 100%
  );
}
.evi-cover-link {
  display: block;
}
.evi-cta {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(255, 255, 255, 0.85);
  color: #111827;
  font-weight: 700;
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  font-size: 0.82rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.evi-tipo {
  position: absolute;
  top: 12px;
  left: 12px;
  border-radius: 999px;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}
.badge-comunidad {
  background: #7c3aed;
}
.badge-institucion {
  background: #2563eb;
}
.badge-fundacion {
  background: #059669;
}
.evi-count {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: #fff;
  color: #111827;
  border-radius: 999px;
  font-weight: 700;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.evi-title {
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 0.25rem;
}
.evi-meta li strong {
  color: #111827;
}

.evi-thumb-link {
  display: block;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eef0f3;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.evi-thumb-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
}
.evi-thumb-img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  display: block;
}
</style>
